import { useMemo } from 'react'

import {
  useWatch,
  useForm,
  useFormContext,
  FormProvider,
} from 'react-hook-form'
import { toast } from 'sonner'

import { ErrorMessages } from '~/components/FormErrorMessages'
import { AddressSearchInput, Message } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { LabelInputContainer } from '~/components/inputs/LabelInputContainer'
import { TokenAmountUSDInput } from '~/components/inputs/custom/TokenAmountUSDInput'
import { RecipientFormValues } from '~/components/smart-accounts/schemas'
import { SupportedChainId } from '~/constants/chains'
import { usePayeeLookupByAddress } from '~/hooks/compliance/usePayeeLookup'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress } from '~/types'

type AddRecipientFormValues = {
  recipientAddress: IAddress
  tokenAmount: string
  usdAmount?: string
}

export const AddRecipientDialog = ({
  existingRecipient,
  existingIndex,
  shouldAutoFocus,
  transferToken,
  addRecipient,
  editRecipient,
  onClose,
}: {
  existingRecipient?: RecipientFormValues
  existingIndex?: number
  shouldAutoFocus: boolean
  transferToken: {
    token: IAddress
    chainId: SupportedChainId
  }
  addRecipient?: (recipient: RecipientFormValues) => void
  editRecipient?: (recipient: RecipientFormValues) => void
  onClose: () => void
}) => {
  const transferForm = useFormContext<TransferFormValues>()

  const tokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
  })

  const dialogForm = useForm<AddRecipientFormValues>({
    defaultValues: existingRecipient
      ? {
          recipientAddress: existingRecipient.receiver.address,
          tokenAmount: existingRecipient.amount.toString(),
          usdAmount: existingRecipient.usdAmount,
        }
      : {
          recipientAddress: '' as IAddress,
          tokenAmount: '',
          usdAmount: undefined,
        },
  })

  const address = useWatch({
    control: dialogForm.control,
    name: 'recipientAddress',
  })
  const tokenAmount = useWatch({
    control: dialogForm.control,
    name: 'tokenAmount',
  })
  const isUSDInput = useWatch({
    control: transferForm.control,
    name: 'isUSDInput',
  })

  const { payee, isLoading: isLoadingPayee } = usePayeeLookupByAddress(address)
  const isCompliantPayment = !!payee && !isLoadingPayee

  const onSubmit = dialogForm.handleSubmit((data) => {
    if (isLoadingPayee) {
      toast.error('Failed to add recipient', {
        description:
          'Still loading recipient information, please refresh and try again',
      })
      throw new Error('Tried to add a recipient while loading information')
    }

    const recipient = {
      receiver: {
        address: data.recipientAddress,
        compliantPaymentRelationId: payee?.id,
      },
      amount: data.tokenAmount.toString(),
      usdAmount: data.usdAmount,
    }

    if (existingIndex !== undefined && editRecipient) {
      editRecipient(recipient)
    } else if (addRecipient) {
      addRecipient(recipient)
    }

    onClose()
  })

  const isEditing = existingIndex !== undefined

  const isDisabled = useMemo(() => {
    const hasRecipient = address
    const hasTokenAmount = parseFloat(tokenAmount) > 0

    return !(hasRecipient && hasTokenAmount)
  }, [address, tokenAmount])

  return (
    <FormProvider {...dialogForm}>
      <SplitsDialogContent
        title={isEditing ? 'Edit recipient' : 'Add recipient'}
        id="add-recipient"
        variant="cta"
        cancelButton={{
          text: 'Cancel',
          onClick: onClose,
          eventName: isEditing
            ? 'clickedCancelEditRecipient'
            : 'clickedCancelAddRecipient',
        }}
        actionButton={{
          text: isEditing ? 'Save' : 'Add',
          onClick: onSubmit,
          isDisabled: isDisabled,
          eventName: isEditing ? 'clickedEditRecipient' : 'clickedAddRecipient',
        }}
      >
        <form onSubmit={onSubmit}>
          <div className="flex flex-col space-y-4">
            <LabelInputContainer label="Recipient" isInputContainer>
              <AddressSearchInput<AddRecipientFormValues>
                control={dialogForm.control}
                inputName="recipientAddress"
                setValue={dialogForm.setValue}
                autoFocus={shouldAutoFocus}
                footer={
                  payee?.payeeInvitedEmail
                    ? `We'll send a payment notification to ${payee.payeeInvitedEmail}`
                    : undefined
                }
              />
            </LabelInputContainer>
            <LabelInputContainer label="Amount" isInputContainer>
              <div className="relative">
                <TokenAmountUSDInput
                  control={dialogForm.control}
                  inputName={'tokenAmount'}
                  usdInputName={'usdAmount'}
                  setValue={dialogForm.setValue}
                  clearErrors={dialogForm.clearErrors}
                  token={transferToken.token}
                  chainId={transferToken.chainId}
                  syncUSDInput={(isUSDInput) => {
                    transferForm.setValue('isUSDInput', isUSDInput)
                  }}
                  unit={isUSDInput ? undefined : tokenMetadata?.symbol}
                />
              </div>
            </LabelInputContainer>
            <ErrorMessages errors={dialogForm.formState.errors} />
          </div>
        </form>
        {isCompliantPayment && (
          <Message title="Compliant payment" variant="info">
            The recipient has been verified, and this payment will be included
            in relevant tax forms
          </Message>
        )}
      </SplitsDialogContent>
    </FormProvider>
  )
}
